import React from 'react';
import { useEffect } from "react";
import { browserName, browserVersion, osName, isMobileOnly, isTablet } from 'react-device-detect';
import { parseISO, differenceInDays } from 'date-fns'
import { getDomainConfig } from '../../../../config/config';
import { dataLayerSession } from './datalayer.session';
import { dataLayerCart } from './datalayer.cart';
import { useCart } from '../../../../hooks/useCart';
import { googleDataLayer} from './googleDataLayer';

const PageView = () => {

    const language = document.documentElement.lang;
    const geoRegion = 'GB';

    useEffect(() => {

        if (getDomainConfig().siteName === "AttractionTickets" || "American Attractions" || "SeaWorld Parks UK") {

            window.digitalData = window.digitalData || {};
            window.digitalData.events = window.digitalData.events || [];

            const destinationUrl = window.location.href;
            const pageID = window.location.pathname.substring(1);
            const pageInstanceID = pageID + '-' + (process.env.NODE_ENV === 'development' ? 'DEV' : 'PROD');
            const responsiveState = isTablet ? 'tablet' : isMobileOnly ? 'mobile' : 'desktop';
            const cleanUrl = window.location.origin + window.location.pathname;

            const match = window.location.hostname.match(/[a-z0-9_-]+\.[a-z]+$/);
            const domain = match ? match[0] : window.location.hostname;
            const subdomain = window.location.hostname;

            const category = {
                pageType: 'Commerce',
                primaryCategory: 'Web Store',
            };

            const page = {
                pageInstanceID,
                pageInfo: {
                    pageID,
                    pageName: 'Checkout',
                    destinationUrl,
                    language,
                    referringURL: '', // empty for now
                    queryParameters: '', //empty for now
                    sysenv: responsiveState,
                    geoRegion,
                },
                category,
                attributes: {
                    cleanUrl,
                    responsiveState,
                    scrollPercent: 0, // Will be zero on page load
                    domain,
                    subdomain,
                    businessObjective: 'CHECKOUT PAGE',
                    contentFocus: 'CHECKOUT PAGE',
                    destination: getDomainConfig().brand,
                    internalReferrerDomain: '', // empty for now
                    referrerPageName: '', //empty for now
                    referrerContentFocus: 'UK Store',
                    ddlCreationTimestamp: new Date().toISOString(),
                }
            };

            const pageLoadInfo = {
                eventInfo: {
                    effect: 'Page Loaded',
                    eventAction: 'Load',
                    eventName: 'Checkout',
                    eventPoints: '', //empty for now
                    eventTargetUrl: '', //empty for now
                    timeStamp: new Date().toISOString(),
                    type: 'Page',
                }
            };

            const platform = {
                version: { technologyName: 'React', technologyVersion: React.version },
                platformBrowser: {
                    name: browserName,
                    os: osName,
                    version: browserVersion,
                },
                platformType: responsiveState,
                sessionNumber: '', //empty for now
                userAgent: navigator.userAgent,
            };
            window.digitalData.page = page;
            window.digitalData.platform = platform;
            window.digitalData.version = '1.0'; // W3C specification version

            // Ensure the "Page Loaded" event only fires once, even if this component re-renders:
            if (!window.digitalData.events.find(e => e.eventInfo.effect === 'Page Loaded')) {
              window.digitalData.events.push(pageLoadInfo);
            }

        }
        return null

    }, []);

    // Set digitalData.user:
    useEffect(() => {

      window.digitalData = window.digitalData || {};

      const visitData = dataLayerSession.getVisitData();

      const user = [
        {
          isAuthenticated: false,

          daysSinceLastVisit: visitData?.daysSinceLastVisit,
          newOrReturning30Days: visitData?.daysSinceLastVisit === undefined ? '' : visitData.daysSinceLastVisit < 30 ? 'returning' : 'new',
          newOrReturning90Days: visitData?.daysSinceLastVisit === undefined ? '' : visitData.daysSinceLastVisit < 90 ? 'returning' : 'new',
          profile: [
            {
              profileInfo: {
                language,
                returningStatus: visitData?.hasLoggedInBefore,
                type: 'Consumer',
              }
            }
          ]
        }
      ];
      window.digitalData.user = user;

    }, []);


    // Set digitalData.cart when cart loads:
    const { isSuccess: cartIsSuccess, data: cartContents } = useCart();
    useEffect(() => {

      if (!cartIsSuccess) return;

      // Google data layer / loop through products and log the checkout event
      const listOfProducts = []
      cartContents.data.tickets.forEach((ticket) => {
        let productsProperty = []
        if (ticket.tags.includes('rooms')){
          productsProperty = googleDataLayer.createProductProperty(ticket.data.attraction_title, ticket.product_id, ticket.price, ticket.qty, ticket.title,ticket.ticket_id, ticket.flex_option, ticket.destination_title, ticket.data.category, ticket.data.model,)
        } else {
          productsProperty = googleDataLayer.createProductProperty(ticket.title, ticket.product_id, ticket.price, ticket.qty, `${ticket.title} ${ticket.internal.type}`, ticket.ticket_id, ticket.internal.price_option_title, ticket.destination_title, ticket.destination_title, ticket.data.model,)
        }
        listOfProducts.push(productsProperty)
      })

      let sendDataLayer = true
      window.dataLayer.forEach((dataLayer) => {
        if(dataLayer.event === 'checkout'){
          sendDataLayer = false
        }
      })
      if (sendDataLayer) {
        googleDataLayer.createCheckoutEvent(listOfProducts)
      }

      const tickets = cartContents?.data?.tickets;
      if (!tickets) return;
      window.digitalData = window.digitalData || {};

      const cart = {
        cartID: cartContents.data.id,
        price: {
          basePrice: cartContents.meta.total_price,
          voucherCode: '', // empty for now
          voucherDiscount: '', // empty for now
          currency: cartContents.meta.sale_cur,
          taxRate: '0.00',
          shipping: '0.00',
          shippingMethod: '',
          priceWithTax: cartContents.meta.total_price, // "basePrice + (basePrice*(1+taxRate))"
          cartTotal: cartContents.meta.total_price, // "Amount of total cart after taxes, discounts, and shipping (e.g. $155.99)"
          subTotal:  cartContents.meta.total_price, // "Amount of total cart after taxes, discounts, and shipping (e.g. $155.99)"
        },
        attributes: {
          totalQty: cartContents.meta.total_count, // "Total number of items in entire cart (e.g. 5)"
          totalViews: dataLayerCart.getNumCartViews(),
          lastViewed: dataLayerCart.getLastCartViewDate()?.toISOString() ?? '',
          deliveryMethod: '',
        },
        item: [],
      }

      tickets.forEach(cartItem => {
        let title = cartItem.title
        let year = cartItem.year || cartItem.date.substring(0,4)
        let productID = cartItem.ticket_id
        let productName = title
        let sku = cartItem.data.model
        let totalGuests = ''
        const tmp = []
        if (cartItem.data.orig_type_ticket) tmp.push(cartItem.data.orig_type_ticket)
        if (cartItem.data.year) tmp.push(cartItem.data.year)
        if (tmp.length) productName = title + ' (' + tmp.join(', ') + ')'
        // Get attribute ids for this cart item. Work around an API bug whereby the
        // "attributes" property is an object unless it's empty, in which case it's an
        // empty array.
        let attributeIds = []
        if (!Array.isArray(cartItem.attributes)) {
          attributeIds = Object.getOwnPropertyNames(cartItem.attributes).map(id => id.toString())
        }
        if(cartItem.tags.includes('rooms')){
          year = cartItem.date.substring(0,4)
          productID = "Hotels | " + title
          productName = cartItem.data.attraction_title
          const roomId = cartItem.data.product_id;
          const roomDeatils = cartItem.data.room_rate_plan;
          sku = cartItem.data.attraction_id
          totalGuests = 'Adults: ' + cartItem.attributes[48] + `, Children: ${cartItem.attributes[49][0] + cartItem.attributes[87][0]}`
        }

        cart.item.push({
          productInfo: {
            productID: productID,
            productName: productName,
            description: '', // Empty for now
            productURL: '', // Empty for now
            productImage: '', // Empty for now
            productThumbnail: '', // Empty for now
            manufacturer: '', // Empty for now
            sku: sku,
            plu: '', // Empty for now
            source: 'UK Store',
            promo: 0,
            ...totalGuests && {totalGuests}
          },
          category: {
            primaryCategory: dataLayerCart.getTicketPrimaryCategory(cartItem.title, attributeIds),
          },
          attributes: {
            packageID: '', // Empty for now
            packageInstanceID: '', // Empty for now
            productAttributes: {}, // Empty for now
            ...cartItem.date && {
              dateSelectedIn: cartItem.date,
              dateSelectedOut: (42 in cartItem.attributes ? cartItem.attributes[42][0] : ''),
              dateSelectedRange: (cartItem.date && 42 in cartItem.attributes) ? differenceInDays(parseISO(cartItem.attributes[42][0]), parseISO(cartItem.date)).toString() : ''
            },
            Qty: cartItem.qty,
            YearSelected: year,
          },
          price: {
            basePrice: parseFloat(cartItem.price).toFixed(2),
            voucherCode: '',
            voucherDiscount: '',
            currency: cartContents.meta.sale_cur,
            taxRate: '0.00',
            shipping: '0.00',
            subTotal: cartItem.price,
            shippingMethod: '',
            priceWithTax: parseFloat(cartItem.price).toFixed(2),
          }
        })
      })
      window.digitalData.cart = cart

    }, [cartIsSuccess, cartContents]);

    return null;
};

export default PageView;
